import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import MetricsCard from './components/MetricsCard';
import QuickUploadWidget from './components/QuickUploadWidget';
import ActivityTimeline from './components/ActivityTimeline';
import SystemStatusPanel from './components/SystemStatusPanel';
import { useAuth } from '../../contexts/AuthContext';
import dashboardDataService from '../../utils/dashboardDataService';
import Icon from '../../components/AppIcon';


const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      totalProducts: 0,
      successfulUploads: 0,
      failedUploads: 0,
      pendingProcesses: 0
    },
    trends: {
      totalProducts: { trend: 'neutral', value: '0%' },
      successfulUploads: { trend: 'neutral', value: '0%' },
      failedUploads: { trend: 'neutral', value: '0%' },
      pendingProcesses: { trend: 'neutral', value: '0%' }
    },
    activities: [],
    systemStatus: {
      apiConnected: false,
      dataSync: false,
      lastSync: null
    },
    notifications: [],
    isLoading: false
  });

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Load real data from localStorage
    const loadDashboardData = () => {
      setDashboardData(prev => ({ ...prev, isLoading: true }));
      
      try {
        const realMetrics = dashboardDataService?.calculateRealMetrics();
        const realTrends = dashboardDataService?.calculateTrends();
        const realActivities = dashboardDataService?.getRealActivityData();
        const systemStatus = dashboardDataService?.getSystemStatus();
        const notifications = dashboardDataService?.getRealNotifications();
        
        setDashboardData({
          metrics: realMetrics,
          trends: realTrends,
          activities: realActivities,
          systemStatus,
          notifications,
          isLoading: false
        });
        
        console.log('Dashboard loaded with real data:', realMetrics);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setDashboardData(prev => ({ ...prev, isLoading: false }));
      }
    };

    loadDashboardData();

    // Update current time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      // Also refresh data every 5 minutes
      if (new Date()?.getMinutes() % 5 === 0) {
        loadDashboardData();
      }
    }, 60000);

    // Refresh when upload history changes (storage events across tabs)
    const onStorage = (e) => {
      if (e?.key === 'bulk_upload_history') {
        loadDashboardData();
      }
      if (e?.key === 'mercadolibre_last_sync' || e?.key === 'dashboard_force_reload') {
        loadDashboardData();
      }
    };
    window.addEventListener('storage', onStorage);

    // Refresh when tab becomes visible
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadDashboardData();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timer);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const handleFileUpload = (files) => {
    console.log('Files uploaded:', files);
    // Handle file upload logic here
  };

  const metricsData = [
    {
      title: 'Productos Totales',
      value: dashboardData?.metrics?.totalProducts > 0 
        ? dashboardData?.metrics?.totalProducts?.toLocaleString('es-ES')
        : '0',
      subtitle: dashboardData?.metrics?.totalProducts > 0 ? 'Cargados en total' : 'Sin productos cargados',
      icon: 'Package',
      trend: dashboardData?.trends?.totalProducts?.trend,
      trendValue: dashboardData?.trends?.totalProducts?.value,
      color: 'primary',
      isLoading: dashboardData?.isLoading
    },
    {
      title: 'Cargas Exitosas',
      value: dashboardData?.metrics?.successfulUploads > 0 
        ? dashboardData?.metrics?.successfulUploads?.toLocaleString('es-ES')
        : '0',
      subtitle: dashboardData?.metrics?.successfulUploads > 0 ? 'Este mes' : 'Sin cargas exitosas',
      icon: 'CheckCircle',
      trend: dashboardData?.trends?.successfulUploads?.trend,
      trendValue: dashboardData?.trends?.successfulUploads?.value,
      color: 'success',
      isLoading: dashboardData?.isLoading
    },
    {
      title: 'Cargas Fallidas',
      value: dashboardData?.metrics?.failedUploads?.toString() || '0',
      subtitle: dashboardData?.metrics?.failedUploads > 0 ? 'Requieren atención' : 'Sin cargas fallidas',
      icon: 'XCircle',
      trend: dashboardData?.trends?.failedUploads?.trend,
      trendValue: dashboardData?.trends?.failedUploads?.value,
      color: 'error',
      isLoading: dashboardData?.isLoading
    },
    {
      title: 'Procesos Pendientes',
      value: dashboardData?.metrics?.pendingProcesses?.toString() || '0',
      subtitle: dashboardData?.metrics?.pendingProcesses > 0 ? 'En cola' : 'Todo procesado',
      icon: 'Clock',
      trend: dashboardData?.trends?.pendingProcesses?.trend,
      trendValue: dashboardData?.trends?.pendingProcesses?.value,
      color: 'warning',
      isLoading: dashboardData?.isLoading
    }
  ];

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8 max-w-md">
          <div className="flex justify-center items-center mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="42px" height="42px" viewBox="0 0 32 33" fill="none">
              <path d="M16 28.5C22.6274 28.5 28 23.1274 28 16.5C28 9.87258 22.6274 4.5 16 4.5C9.37258 4.5 4 9.87258 4 16.5C4 23.1274 9.37258 28.5 16 28.5Z" stroke="#343330" strokeWidth="2" strokeMiterlimit="10" />
              <path d="M11.5 15.5C12.3284 15.5 13 14.8284 13 14C13 13.1716 12.3284 12.5 11.5 12.5C10.6716 12.5 10 13.1716 10 14C10 14.8284 10.6716 15.5 11.5 15.5Z" fill="#343330" />
              <path d="M20.5 15.5C21.3284 15.5 22 14.8284 22 14C22 13.1716 21.3284 12.5 20.5 12.5C19.6716 12.5 19 13.1716 19 14C19 14.8284 19.6716 15.5 20.5 15.5Z" fill="#343330" />
              <path d="M21 22.5C19.9625 20.7062 18.2213 19.5 16 19.5C13.7787 19.5 12.0375 20.7062 11 22.5" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="text-2xl font-medium text-neutral-800 mb-2">No hay usuario autenticado</h1>
          <p className="text-neutral-600 text-base mb-4">Por favor, inicia sesión para acceder al panel de control.</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-primary hover:bg-primary/80 text-white font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200 shadow-sm"
          >
            <Icon name="ArrowLeft" size={18} color="#fff" />
            Ir a Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Panel de Control - MercadoLibre Bulk Publisher</title>
        <meta name="description" content="Panel de control para gestión masiva de productos en MercadoLibre. Monitorea cargas, estadísticas y estado del sistema." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-6 pt-10 pb-8">
            {/* Header Section */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-text-primary mb-2">
                    Panel de Control
                  </h1>
                  <p className="text-text-secondary">
                    {dashboardData?.metrics?.totalProducts > 0 
                      ? `Gestiona tus ${dashboardData?.metrics?.totalProducts} productos cargados`
                      : 'Comienza cargando tus primeros productos'
                    }
                  </p>
                </div>
                <div className="mt-4 sm:mt-0 flex flex-col items-end">
                  <div className="text-sm text-text-secondary">
                    Última actualización: {currentTime?.toLocaleTimeString('es-ES', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                  {dashboardData?.systemStatus?.lastSync && (
                    <div style={{ display: 'none' }} className="text-xs text-text-secondary mt-1">
                      Última sincronización: {new Date(dashboardData?.systemStatus?.lastSync)?.toLocaleDateString('es-ES')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {metricsData?.map((metric, index) => (
                <MetricsCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  subtitle={metric?.subtitle}
                  icon={metric?.icon}
                  trend={metric?.trend}
                  trendValue={metric?.trendValue}
                  color={metric?.color}
                  isLoading={metric?.isLoading}
                />
              ))}
            </div>

            {/* Main Content Grid - Simplified layout without duplicate upload sections */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              {/* Left Column - Quick Upload Widget Only */}
              <div className="lg:col-span-1">
                <QuickUploadWidget onFileUpload={handleFileUpload} />
              </div>

              {/* Middle Column - Activity Timeline */}
              <div className="lg:col-span-1">
                <ActivityTimeline activities={dashboardData?.activities} />
              </div>

              {/* Right Column - System Status (notificaciones movidas al sidebar) */}
              <div className="lg:col-span-1 space-y-6">
                <SystemStatusPanel systemStatus={dashboardData?.systemStatus} />
              </div>
            </div>

            {/* Footer Info - Updated with real status */}
            <div className="mt-8 pt-6 border-t border-border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-text-secondary">
                <div>
                  <p>MercadoLibre Bulk Publisher v2.1.0</p>
                  <p className="mt-1">
                    © {new Date()?.getFullYear()} - Datos actualizados en tiempo real
                  </p>
                </div>
                <div className="mt-4 sm:mt-0">
                  <p className={`${dashboardData?.systemStatus?.apiConnected ? 'text-success' : 'text-warning'}`}>
                    {dashboardData?.systemStatus?.apiConnected ? 'Sistema Activo' : 'Sin datos de actividad'}
                  </p>
                  <p className="mt-1">Región: América Latina</p>
                </div>
              </div>
            </div>
          </div>
    </>
  );
};

export default Dashboard;